import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ECOSYSTEM, KIND_LABEL, type ItemKind } from "@/lib/ecosystem";
import { absoluteUrl } from "@/lib/site";

export const Route = createFileRoute("/map")({
  head: () => ({
    meta: [
      { title: "Knowledge Map · Buddha KA" },
      { name: "description", content: "A living map of every song, teaching, activation, and book in the KAfrequency ecosystem." },
      { property: "og:title", content: "Knowledge Map · Buddha KA" },
      { property: "og:description", content: "One field. Four kinds. Every node connected." },
      { property: "og:url", content: absoluteUrl("/map") },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/map") }],
  }),
  component: MapPage,
});

const KIND_ORDER: ItemKind[] = ["music", "wisdom", "activation", "book"];
const KIND_ANGLE: Record<ItemKind, number> = {
  music: -90,
  wisdom: 0,
  activation: 90,
  book: 180,
};
const KIND_COLOR: Record<ItemKind, string> = {
  music: "var(--gold-luminous)",
  wisdom: "var(--ivory)",
  activation: "var(--emerald)",
  book: "var(--violet)",
};

function MapPage() {
  const [filter, setFilter] = useState<"all" | ItemKind>("all");
  const [hover, setHover] = useState<string | null>(null);

  const size = 720;
  const cx = size / 2;
  const cy = size / 2;

  const layout = useMemo(() => {
    // Group by kind, place each group as an arc around the center.
    const grouped: Record<ItemKind, typeof ECOSYSTEM> = {
      music: [], wisdom: [], activation: [], book: [],
    };
    for (const item of ECOSYSTEM) grouped[item.kind].push(item);

    const nodes: Array<{ key: string; x: number; y: number; item: (typeof ECOSYSTEM)[number] }> = [];
    KIND_ORDER.forEach((kind) => {
      const arr = grouped[kind];
      const baseAngle = KIND_ANGLE[kind];
      const spread = 60; // degrees across which the group fans out
      const count = arr.length;
      arr.forEach((item, i) => {
        const t = count === 1 ? 0 : i / (count - 1) - 0.5;
        const angleDeg = baseAngle + t * spread;
        const rad = (angleDeg * Math.PI) / 180;
        // vary radius slightly to avoid a perfect ring
        const r = 260 + ((i % 3) - 1) * 22;
        nodes.push({
          key: `${item.kind}:${item.slug}`,
          x: cx + Math.cos(rad) * r,
          y: cy + Math.sin(rad) * r,
          item,
        });
      });
    });
    return nodes;
  }, []);

  const shown = filter === "all" ? layout : layout.filter((n) => n.item.kind === filter);
  const hovered = hover ? layout.find((n) => n.key === hover) : null;

  return (
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
      <header className="text-center" data-reveal="up">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">The Field</p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl">Knowledge Map</h1>
        <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
          Every song, teaching, activation, and book — one connected body of work.
        </p>
      </header>

      <div className="mt-8 flex flex-wrap justify-center gap-2" data-reveal="up">
        {(["all", ...KIND_ORDER] as const).map((k) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
              filter === k
                ? "border-gold bg-gold text-obsidian"
                : "hairline text-muted-foreground hover:border-gold hover:text-gold"
            }`}
          >
            {k === "all" ? "All" : KIND_LABEL[k]}
          </button>
        ))}
      </div>

      <div className="relative mt-8" data-reveal="up">
        <svg viewBox={`0 0 ${size} ${size}`} className="w-full" role="img" aria-label="Knowledge map of the KAfrequency ecosystem">
          {/* concentric guides */}
          <g className="text-gold">
            <circle cx={cx} cy={cy} r={260} fill="none" stroke="currentColor" strokeOpacity="0.08" />
            <circle cx={cx} cy={cy} r={180} fill="none" stroke="currentColor" strokeOpacity="0.06" />
            <circle cx={cx} cy={cy} r={100} fill="none" stroke="currentColor" strokeOpacity="0.05" />
            <text x={cx} y={cy - 4} textAnchor="middle" className="fill-current text-[11px] uppercase tracking-[0.4em]">Buddha</text>
            <text x={cx} y={cy + 14} textAnchor="middle" className="fill-current text-[11px] uppercase tracking-[0.4em]">KA</text>
          </g>

          {/* threads from center to each shown node */}
          {shown.map((n) => (
            <line
              key={`l-${n.key}`}
              x1={cx} y1={cy} x2={n.x} y2={n.y}
              stroke={KIND_COLOR[n.item.kind]}
              strokeOpacity={hover === n.key ? 0.6 : 0.12}
              strokeWidth={hover === n.key ? 1.5 : 0.5}
            />
          ))}

          {/* nodes */}
          {shown.map((n) => (
            <g
              key={n.key}
              onPointerEnter={() => setHover(n.key)}
              onPointerLeave={() => setHover((h) => (h === n.key ? null : h))}
              onFocus={() => setHover(n.key)}
              onBlur={() => setHover((h) => (h === n.key ? null : h))}
            >
              <Link to={n.item.hrefRoute} params={{ slug: n.item.slug }} aria-label={n.item.title}>
                {/* Larger transparent hit target for touch */}
                <circle cx={n.x} cy={n.y} r={22} fill="transparent" />
                <circle
                  cx={n.x} cy={n.y}
                  r={hover === n.key ? 9 : 5}
                  fill={KIND_COLOR[n.item.kind]}
                  fillOpacity={hover === n.key ? 1 : 0.7}
                  stroke="var(--obsidian)"
                  strokeWidth={1}
                  className="transition-all duration-200"
                />
              </Link>
            </g>
          ))}
        </svg>

        {hovered && (
          <div className="pointer-events-none absolute left-1/2 top-4 -translate-x-1/2 rounded-full border hairline bg-charcoal/90 px-4 py-2 text-xs backdrop-blur-md">
            <span className="uppercase tracking-[0.3em] text-gold">{KIND_LABEL[hovered.item.kind]}</span>{" "}
            <span className="text-ivory">· {hovered.item.title}</span>
          </div>
        )}
      </div>

      <p className="mt-4 text-center text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
        Tap a node to reveal · Tap again to enter
      </p>
    </div>
  );
}
